declare const I18n;

import React, { useEffect, useRef } from "react";
import TextInput from "./inputs/TextInput";
import DropdownInput from "./inputs/DropdownInput";
import PasswordInput from "./inputs/PasswordInput";
import BooleanInput from "./inputs/BooleanInput";
import { getByPath } from "@/utils/getByPath";
import { isValidEmail } from "@/utils/validateEmail";

import { evaluateConditions } from "@/utils/evaluateConditions";

type Props = {
  block: any;
  initialData: any;
  data: Record<string, any>;
  onChange: (
    key: string,
    value: any,
    options?: { remove?: boolean; groupKey?: string; fieldsGroupKey?: string }
  ) => void;
};

const BasicInputs: React.FC<Props> = ({ block, initialData, data, onChange }) => {
  const sectionGroup = block.inputGroup;
  const initializedKeys = useRef<Set<string>>(new Set());
  const writeValue = (key: string, value: any) => {
    onChange(key, value, { groupKey: sectionGroup });
  };

  const getFieldPath = (key: string) => {

    if (sectionGroup) return `${sectionGroup}.${key}`;
    return key;
  };

  const readValue = (key: string, useInitialData: boolean = false) => {
    const path = getFieldPath(key);
    if (useInitialData) {
      return getByPath(initialData, path)
    }

    return getByPath(data, path);
  };

  useEffect(() => {
    block.props.fields.forEach((field: any) => {
      if (field.type === "boolean") {
        const fullPath = getFieldPath(field.key);
        if (initializedKeys.current.has(fullPath)) return;
        const value = getByPath(data, fullPath);
        if (value === undefined || value === null) {
          initializedKeys.current.add(fullPath);
          writeValue(field.key, false);
        }
      }
    });
  }, [block]);

  return (
    <div
      style={{
        display: "flex",
        gap: "1rem",
        justifyContent: "center",
      }}
    >
      {block.props.fields.map((field: any) => {
        if (field.showIf && !evaluateConditions(field.showIf, data)) {
          return null;
        }

        const value = readValue(field.key, field.useInitialData);

        const label = field.labelKey ? I18n.t(field.labelKey) : field.label;
        const subLabel = field.subLabelKey
          ? I18n.t(field.subLabelKey)
          : field.subLabel;

        switch (field.type) {
          case "text":
            let error: string | undefined;

            if (value && field.subtype === "email" && !isValidEmail(value)) {
              error = I18n.t("views.onboarding.messaging.errors.invalid_email", { count: field.minLength });
            }

            if (
              value &&
              field.minLength &&
              value?.length < field.minLength
            ) {
              error = I18n.t("views.onboarding.messaging.errors.min_characters", { count: field.minLength });
            }

            return (
              <TextInput
                key={field.key}
                label={label}
                value={value ?? ""}
                onChange={(val) => writeValue(field.key, val)}
                maxLength={field.maxLength}
                subLabel={subLabel}
                error={error}
                isRequired={field.isRequired === true || field.isRequired === "true"}
                isEditable={!(field.isEditable === false || field.isEditable === "false")}
                allowCopy={field.allowCopy === true || field.allowCopy === "true"}
              />
            );

          case "dropdown":
            return (
              <DropdownInput
                key={field.key}
                label={label}
                value={value ?? ""}
                options={field.options}
                onChange={(val) => writeValue(field.key, val)}
                isRequired={field.isRequired === true || field.isRequired === "true"}
              />
            );

          case "password": {
            const password = readValue("password");
            const confirmPassword = readValue("password_confirmation");

            let error: string | undefined;
            if (
              field.key === "password" &&
              field.minLength &&
              password?.length < field.minLength
            ) {
              error = I18n.t("models.user.messaging.password_length");
            }

            if (
              field.key === "password_confirmation" &&
              confirmPassword?.length > 0 &&
              password !== confirmPassword
            ) {
              error = I18n.t("models.user.messaging.password_not_matching");
            }

            return (
              <PasswordInput
                key={field.key}
                label={label}
                value={value ?? ""}
                onChange={(val) => writeValue(field.key, val)}
                minLength={field.minLength}
                subLabel={subLabel}
                error={error}
                isEditable={
                  !(field.isEditable === false || field.isEditable === "false")
                }
              />
            );
          }

          case "boolean":
            return (
              <BooleanInput
                key={field.key}
                labelKey={field.labelKey}
                label={field.label}
                name={field.key}
                fullWidth={field.fullWidth}
                checked={value === true}
                onChange={(checked) => writeValue(field.key, checked)}
              />
            );

          default:
            return null;
        }
      })}
    </div>
  );
};

export default BasicInputs;
